/**
 * Section Navigator — Left panel showing the section list with add/remove/reorder.
 */

import { useState } from 'react';
import {
  Plus,
  Trash2,
  Copy,
  GripVertical,
  // ChevronDown,
  // ChevronRight,
  Layout,
  Star,
  Users,
  MessageSquare,
  DollarSign,
  HelpCircle,
  Phone,
  BarChart3,
  Zap,
  Info,
  Image,
  Mail,
} from 'lucide-react';

interface Section {
  id: string;
  type: string;
  order: number;
  content: Record<string, unknown>;
}

interface SectionNavigatorProps {
  sections: Section[];
  selectedSection: string | null;
  onSelectSection: (id: string | null) => void;
  onAddSection: (type: string, position: number) => void;
  onRemoveSection: (id: string) => void;
  onCloneSection: (id: string) => void;
  onReorder: (order: string[]) => void;
}

const SECTION_ICONS: Record<string, any> = {
  header: Layout,
  hero: Star,
  features: Zap,
  about: Info,
  testimonials: MessageSquare,
  pricing: DollarSign,
  faq: HelpCircle,
  contact: Phone,
  cta: Mail,
  stats: BarChart3,
  team: Users,
  portfolio: Image,
  footer: Layout,
};

const AVAILABLE_SECTIONS = [
  { type: 'hero', label: 'Hero' },
  { type: 'features', label: 'Features' },
  { type: 'about', label: 'About' },
  { type: 'testimonials', label: 'Testimonials' },
  { type: 'pricing', label: 'Pricing' },
  { type: 'faq', label: 'FAQ' },
  { type: 'cta', label: 'Call to Action' },
  { type: 'contact', label: 'Contact' },
  { type: 'stats', label: 'Stats' },
  { type: 'team', label: 'Team' },
  { type: 'portfolio', label: 'Portfolio' },
];

export function SectionNavigator({
  sections,
  selectedSection,
  onSelectSection,
  onAddSection,
  onRemoveSection,
  onCloneSection,
  onReorder,
}: SectionNavigatorProps) {
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newSections = [...sections];
    const [dragged] = newSections.splice(draggedIndex, 1);
    newSections.splice(index, 0, dragged);
    onReorder(newSections.map((s) => s.id));
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="flex w-56 flex-col overflow-y-auto border-r bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-3 py-2.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">
          Sections
        </span>
        <button
          onClick={() => setShowAddMenu(!showAddMenu)}
          className="flex h-6 w-6 items-center justify-center rounded bg-blue-50 text-blue-600 transition-colors hover:bg-blue-100"
          title="Add section"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Add section menu */}
      {showAddMenu && (
        <div className="border-b bg-gray-50 p-2">
          <p className="mb-2 px-1 text-xs font-medium text-gray-500">Add Section</p>
          <div className="grid grid-cols-2 gap-1">
            {AVAILABLE_SECTIONS.map((s) => {
              const Icon = SECTION_ICONS[s.type] || Layout;
              return (
                <button
                  key={s.type}
                  onClick={() => {
                    onAddSection(s.type, sections.length);
                    setShowAddMenu(false);
                  }}
                  className="flex items-center gap-1.5 rounded px-2 py-1.5 text-xs text-gray-600 transition-all hover:bg-white hover:shadow-sm"
                >
                  <Icon className="h-3 w-3" />
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Section list */}
      <div className="flex-1 overflow-y-auto py-1">
        {sections.map((section, index) => {
          const Icon = SECTION_ICONS[section.type] || Layout;
          const isSelected = selectedSection === section.id;

          return (
            <div
              key={section.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              onClick={() => onSelectSection(isSelected ? null : section.id)}
              className={`group mx-1.5 my-0.5 flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 transition-colors ${
                isSelected
                  ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                  : 'text-gray-700 hover:bg-gray-50'
              } ${draggedIndex === index ? 'opacity-50' : ''}`}
            >
              <GripVertical className="h-3 w-3 flex-shrink-0 cursor-grab text-gray-300" />
              <Icon className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="flex-1 truncate text-xs font-medium capitalize">
                {section.id.replace(/-/g, ' ')}
              </span>

              {/* Actions (visible on hover) */}
              <div className="hidden items-center gap-0.5 group-hover:flex">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCloneSection(section.id);
                  }}
                  className="rounded p-0.5 hover:bg-gray-200"
                  title="Clone"
                >
                  <Copy className="h-3 w-3 text-gray-400" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveSection(section.id);
                  }}
                  className="rounded p-0.5 hover:bg-red-50"
                  title="Remove"
                >
                  <Trash2 className="h-3 w-3 text-red-400" />
                </button>
              </div>
            </div>
          );
        })}

        {sections.length === 0 && (
          <p className="px-3 py-8 text-center text-xs text-gray-400">
            No sections yet. Click + to add one.
          </p>
        )}
      </div>
    </div>
  );
}
